/**
 * Whose clock a timeline plays on (2026-10-25).
 *
 * Operator, 2026-10-25: "i pressed play in the timeline editor on the mac, it didn't sync up ... i need the timeline
 * editor to be perfectly in sync with ableton/opbridge". Measured: the kick/snare chase played on STREAMPC (its lights
 * are there, DEC-224), and STREAMPC's OPBridge ran its own internal clock with no tie to Live on the Mac. The editor's
 * playhead followed STREAMPC faithfully, so it looked "working, just out of sync", and nothing on screen said why.
 * `.notepads/timeline-pro-grade.md`, 2026-10-25.
 *
 * So the "Plays on" pill also says whose clock that computer is on, read from its own `GET /clock/status` (the
 * `source`, and OPBridge DEC-685's `mesh` block while the mesh clock is on there). It warns only where it matters: a
 * computer on its own clock in a rig of two or more, where two clocks really do drift apart; a lone computer with no
 * Ableton is in time with itself and is told so plainly.
 */
import { createEffect, createSignal, onCleanup, untrack, type Accessor } from 'solid-js';
import type { ClockStatus } from '../opbridge/httpClient.ts';
import { getConnection } from '../opbridge/store.ts';

/** How often a computer's clock is read while something shows it. */
const CLOCK_READ_MS = 2000;

/** One reading per computer, shared by everything showing it (the "Plays on" pill and the editor's Launch both read the
 *  same computer: two pollers doubled the reads of a Mac that already refuses some for rate). */
interface SharedRead { refs: number; status: Accessor<ClockStatus | null>; timer: ReturnType<typeof setInterval> }
const sharedReads = new Map<string, SharedRead>();

function joinRead(target: string): SharedRead {
  const found = sharedReads.get(target);
  if (found) { found.refs++; return found; }
  const [status, setStatus] = createSignal<ClockStatus | null>(null);
  const read = async () => {
    const conn = getConnection(target);
    if (!conn || conn.status() !== 'connected') { setStatus(null); return; }
    const s = await conn.httpClient().clockStatus().catch(() => null);
    // A failed read says nothing about the clock: keep the last one rather than flicker.
    if (s && sharedReads.get(target) === entry) setStatus(s);
  };
  const entry: SharedRead = { refs: 1, status, timer: setInterval(() => { void read(); }, CLOCK_READ_MS) };
  sharedReads.set(target, entry);
  // Its first, synchronous part reads the connection's status: not a dependency of the caller's effect.
  untrack(() => { void read(); });
  return entry;
}

function leaveRead(target: string, entry: SharedRead): void {
  entry.refs--;
  if (entry.refs > 0) return;
  clearInterval(entry.timer);
  if (sharedReads.get(target) === entry) sharedReads.delete(target);
}

/** The `/clock/status` of the computer `id()` names (a row id), read every 2 s while anything shows it; null until read
 *  or while that computer is not connected. */
export function createClockStatus(id: Accessor<string | null>): Accessor<ClockStatus | null> {
  const [entry, setEntry] = createSignal<SharedRead | null>(null);
  createEffect(() => {
    const target = id();
    if (!target) { setEntry(null); return; }
    const e = joinRead(target);
    setEntry(e);
    onCleanup(() => leaveRead(target, e));
  });
  return () => entry()?.status() ?? null;
}

/**
 * Live is THERE and STOPPED, on this computer's clock or on the one it follows (2026-10-28). Only then do Play and
 * Launch differ: Play runs at once at Live's last tempo and moves onto Live's bar lines when Live plays; Launch waits for
 * Live's first bar line. "There" = OP-BridgeSync's markers are arriving (it sends one every 500 ms while Live is stopped;
 * OPBridge calls it gone after 5 s without one, S342). With Live gone, either start runs at the timeline's own tempo.
 */
export function liveIsStopped(s: ClockStatus): boolean {
  if (s.playing) return false;
  const mesh = s.mesh?.enabled ? s.mesh : undefined;
  if (mesh?.role === 'following' && mesh.following) {
    if (mesh.following.leaderLost) return false;
    return isAbleton(mesh.peers?.find((p) => p.nodeId === mesh.following!.leader)?.source);
  }
  return isAbleton(s.source) && !s.stale;
}

export type ClockKind = 'ableton' | 'ableton-quiet' | 'follows-ableton' | 'follows' | 'leader-lost' | 'song' | 'own' | 'timeline';

export interface ClockVerdict {
  kind: ClockKind;
  /** A few words for the pill, after "Plays on <name>, ". */
  short: string;
  /** One or two plain sentences for its tooltip. */
  detail: string;
  /** Amber: the timeline is probably not in time with Live. */
  warn: boolean;
}

const isAbleton = (source: string | null | undefined): boolean => !!source && source.startsWith('ableton');

/**
 * What `status` (the home computer's `/clock/status`) means for a timeline playing there. `home` is that computer's
 * name, `rigSize` how many computers this app knows, `nameOf` a node id's name (null when unknown).
 */
export function clockVerdict(status: ClockStatus, home: string, rigSize: number, nameOf: (nodeId: string) => string | null): ClockVerdict {
  // A timeline leads the clock (OPBridge S342, 2026-10-28): Live is not open, so this computer runs the clock at the
  // playing timeline's own tempo and the others follow it. Its saved source still names Ableton (stale), so without
  // this it read "Ableton's clock has gone quiet", amber, at exactly the moment it was working as asked.
  const lead = status.mesh?.timelineLead;
  if (lead?.active) {
    const bpm = typeof lead.bpm === 'number' ? `${Math.round(lead.bpm * 100) / 100} BPM` : 'its own tempo';
    return {
      kind: 'timeline', short: `the timeline sets the tempo, ${bpm}`, warn: false,
      detail: `Live isn't open, so ${home} runs the clock at the playing timeline's own tempo (${bpm}) and your other computers follow it. When Live plays, the timeline moves onto Live's bar lines.`,
    };
  }
  const mesh = status.mesh?.enabled ? status.mesh : undefined;
  if (mesh?.role === 'following' && mesh.following) {
    const leaderId = mesh.following.leader;
    const leader = nameOf(leaderId) ?? 'another computer';
    if (mesh.following.leaderLost) {
      return {
        kind: 'leader-lost', short: `lost ${leader}'s clock`, warn: true,
        detail: `${home} follows ${leader}'s clock, but has not heard it for a while, so it runs on by itself and may drift. Check that ${leader} is on and connected.`,
      };
    }
    // The leader runs the clock at a timeline's tempo, Live being gone (OPBridge S342's follower field).
    const led = mesh.following.timelineLead;
    if (led?.active) {
      const bpm = typeof led.bpm === 'number' ? `${Math.round(led.bpm * 100) / 100} BPM` : 'its own tempo';
      return {
        kind: 'timeline', short: `following ${leader}, where the timeline sets the tempo, ${bpm}`, warn: false,
        detail: `${home} follows ${leader}'s clock. Live isn't open, so ${leader} runs it at the playing timeline's own tempo (${bpm}). When Live plays, the timeline moves onto Live's bar lines.`,
      };
    }
    const leaderSource = mesh.peers?.find((p) => p.nodeId === leaderId)?.source;
    if (isAbleton(leaderSource)) {
      return {
        kind: 'follows-ableton', short: `in time with Ableton on ${leader}`, warn: false,
        detail: `${home} follows ${leader}, which follows Ableton, so this timeline lands on Live's bar lines.`,
      };
    }
    return {
      kind: 'follows', short: `following ${leader}'s clock`, warn: false,
      detail: `${home} follows ${leader}'s clock. ${leader} is not on Ableton's clock right now, so the timeline is in time with ${leader}, not with Live.`,
    };
  }
  if (isAbleton(status.source)) {
    if (status.stale) {
      return {
        kind: 'ableton-quiet', short: "Ableton's clock has gone quiet", warn: true,
        detail: `${home} takes its clock from Ableton, but nothing has come from Live for a while (Live closed, or its sync device not loaded), so the timeline may drift from Live.`,
      };
    }
    return {
      kind: 'ableton', short: "on Ableton's clock", warn: false,
      detail: `${home} follows Ableton, so this timeline lands on Live's bar lines.`,
    };
  }
  if (status.source === 'test_track') {
    return {
      kind: 'song', short: "on its song's clock", warn: false,
      detail: `${home} is playing a song, and the clock follows that song.`,
    };
  }
  const multi = rigSize >= 2;
  const fix = mesh
    ? ' Its mesh clock is on but has found no computer to follow yet.'
    : ' The fix is for every computer to follow the one running Live (OPBridge\'s mesh clock, not switched on on this computer).';
  return {
    kind: 'own', short: multi ? "own clock, not Ableton's" : 'own clock', warn: multi,
    detail: multi
      ? `${home} keeps its own clock, so this timeline is not in time with Ableton or your other computers.${fix}`
      : `${home} keeps its own clock. That is fine on its own; with Ableton, have it follow Live's clock to land on Live's bar lines.`,
  };
}
